import { MongoObjectIdSchema } from '@terra/shared-utils';
import { z } from 'zod';
import { normalizeApplicationInteractionStatus } from './application-interaction-status';

export const ApplicationInteractionCategorySchema = z.enum([
  'DISCOVERY',
  'AUTHENTICATION',
  'AUTOMATED_TESTING',
  'INTERACTIVE_TESTING',
  'AI_TARGET',
  'REACHABILITY',
]);

export const ApplicationInteractionActivityTypeSchema = z.enum([
  'CRAWL',
  'DIRECTORY_DISCOVERY',
  'FEATURE_ANALYSIS',
  'RECON',
  'ENDPOINT_PROBE',
  'MANUAL_BROWSING',
  'TORCH_BROWSER',
  'TORCH_REPLAY',
  'TORCH_REPEATER',
  'CREDENTIAL_VERIFICATION',
  'AUTHENTICATION_DISCOVERY',
  'NEO_EXECUTION',
  'FINDING_RETEST',
  'DYNAMIC_SCAN',
  'WAF_CHECK',
  'PREVENTION_TEST',
  'UNBLOCK_REPRODUCTION',
  'COPILOT_TESTING',
  'AI_DISCOVERY',
  'MCP_PROBE',
  'EN_SCAN',
  'IN_OPERATION',
  'REACHABILITY_CHECK',
  'OOB_CALLBACK',
]);

export const ApplicationInteractionTriggerSchema = z.enum([
  'SCHEDULED',
  'USER',
  'SYSTEM',
  'RETEST',
  'ADAPTIVE',
]);

export const ApplicationInteractionStatusSchema = z.enum([
  'SUCCEEDED',
  'FAILED',
  'BLOCKED',
  'PARTIAL',
  'UNKNOWN',
]);

export const ApplicationInteractionOriginSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('CLOUD'),
    service: z.string().min(1),
  }),
  z.object({
    kind: z.literal('TORCH'),
  }),
]);

export const ApplicationInteractionSessionRefSchema = z.object({
  type: z.string().min(1),
  id: z.string().min(1),
});

export const ApplicationInteractionEvidenceRefSchema = z.object({
  kind: z.enum(['har_entry', 'tool_execution_har']),
  id: z.string().min(1),
});

export const ApplicationInteractionDtoSchema = z.object({
  id: MongoObjectIdSchema,
  tenantId: z.string().min(1),
  applicationId: MongoObjectIdSchema,
  occurredAt: z.string(),
  durationMs: z.number().int().nonnegative().optional(),
  category: ApplicationInteractionCategorySchema,
  activityType: ApplicationInteractionActivityTypeSchema,
  trigger: ApplicationInteractionTriggerSchema,
  status: ApplicationInteractionStatusSchema,
  method: z.string().optional(),
  url: z.string().optional(),
  host: z.string().optional(),
  path: z.string().optional(),
  protocol: z.string().optional(),
  sessionRef: ApplicationInteractionSessionRefSchema.optional(),
  credentialId: MongoObjectIdSchema.optional(),
  mcpIdentityId: MongoObjectIdSchema.optional(),
  initiatingUserId: z.string().min(1).optional(),
  origin: ApplicationInteractionOriginSchema,
  evidenceRef: ApplicationInteractionEvidenceRefSchema.optional(),
});

const ApplicationInteractionUpsertBodySchema =
  ApplicationInteractionDtoSchema.omit({ id: true }).extend({
    sourceKey: z.string().min(1),
  });

export const ApplicationInteractionUpsertDtoSchema = z.preprocess((raw) => {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }

  const item = raw as Record<string, unknown>;
  const statusCode =
    typeof item.statusCode === 'number' ? item.statusCode : undefined;
  const status = normalizeApplicationInteractionStatus({
    status: item.status,
    outcome: item.outcome,
    statusCode,
  });
  const {
    outcome: _outcome,
    statusCode: _statusCode,
    destination: _destination,
    summary: _summary,
    ...rest
  } = item;

  return { ...rest, status };
}, ApplicationInteractionUpsertBodySchema);

export const ApplicationInteractionListQuerySchema = z.object({
  skip: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(100),
  id: MongoObjectIdSchema.optional(),
  occurredAtFrom: z.string().datetime().optional(),
  occurredAtTo: z.string().datetime().optional(),
  durationMs: z.coerce.number().int().nonnegative().optional(),
  category: ApplicationInteractionCategorySchema.optional(),
  activityType: ApplicationInteractionActivityTypeSchema.optional(),
  trigger: ApplicationInteractionTriggerSchema.optional(),
  status: ApplicationInteractionStatusSchema.optional(),
  method: z.string().min(1).optional(),
  url: z.string().min(1).optional(),
  host: z.string().min(1).optional(),
  path: z.string().min(1).optional(),
  protocol: z.string().min(1).optional(),
  sessionRefType: z.string().min(1).optional(),
  sessionRefId: z.string().min(1).optional(),
  credentialId: MongoObjectIdSchema.optional(),
  mcpIdentityId: MongoObjectIdSchema.optional(),
  initiatingUserId: z.string().min(1).optional(),
  originKind: z.enum(['CLOUD', 'TORCH']).optional(),
  cloudService: z.string().min(1).optional(),
  evidenceRefKind:
    ApplicationInteractionEvidenceRefSchema.shape.kind.optional(),
  evidenceRefId: z.string().min(1).optional(),
});

export const ApplicationInteractionListResponseSchema = z.object({
  data: z.array(ApplicationInteractionDtoSchema),
  total: z.number(),
  skip: z.number(),
  limit: z.number(),
});

export const ApplicationInteractionsBatchPayloadSchema = z.object({
  items: z.array(ApplicationInteractionUpsertDtoSchema).max(200),
});

export type ApplicationInteractionCategory = z.infer<
  typeof ApplicationInteractionCategorySchema
>;
export type ApplicationInteractionActivityType = z.infer<
  typeof ApplicationInteractionActivityTypeSchema
>;
export type ApplicationInteractionTrigger = z.infer<
  typeof ApplicationInteractionTriggerSchema
>;
export type ApplicationInteractionStatus = z.infer<
  typeof ApplicationInteractionStatusSchema
>;
export type ApplicationInteractionOrigin = z.infer<
  typeof ApplicationInteractionOriginSchema
>;
export type ApplicationInteractionDto = z.infer<
  typeof ApplicationInteractionDtoSchema
>;
export type ApplicationInteractionUpsertDto = z.infer<
  typeof ApplicationInteractionUpsertDtoSchema
>;
export type ApplicationInteractionListQuery = z.infer<
  typeof ApplicationInteractionListQuerySchema
>;
export type ApplicationInteractionListResponse = z.infer<
  typeof ApplicationInteractionListResponseSchema
>;
